import React, { useEffect, useState } from 'react';
import { useParams, useLoaderData, useSearchParams } from 'react-router-dom';
import { Dropdown } from '../../components/PrimereactComponents';
import { useTranslation } from 'react-i18next';
import MemberCardWrapper from '../../components/common/MemberCardWrapper/MemberCardWrapper';
import { getColors, URLKeys } from '../../Constants';
import ContentSection from '../../components/common/ContentSection/ContentSection';
import StatChip from '../../components/common/StatChip/StatChip';
import { DropdownChangeEvent } from 'primereact/dropdown';
import type { DropdownOptions, PartiesData, PartyMember } from '../../api';
import { useAppContext } from '../../context/AppContext';
import { capitalizeFirstLetter } from '../../utils/texts';
import { useLocalizationLabels } from '../../context/LocalizationLabelContext';

type PartyWithDerivedData = PartiesData & {
    aktivni: boolean;
    pocet_mandatu: number;
};

function transformMemberData(member: PartyMember, partiesData: PartiesData[]) {
    const nameParts = member.jmeno.split(' ');
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(' ');

    // Should probably be done on BE
    let newPartyData = null;
    if (!member.aktivni) {
        partiesData.forEach((party) => {
            const foundMember = party.clenove.find((clen) => clen.id === member.id && clen.aktivni);
            if (foundMember && foundMember.aktivni) {
                newPartyData = {
                    partyName: party.strana_zkratka,
                    partyColor: party.strana_barva
                };
            }
        });
    }

    return {
        id: member.id,
        firstName: firstName,
        lastName: lastName,
        inactive: !member.aktivni,
        gender: member.pohlavi as 0 | 1, // API documents pohlavi as 0=male,1=female
        photo: member.photo,
        newPartyData
    };
}
const findOptionById = (arrayOfOptions: DropdownOptions[], idToFind: number) => {
    return arrayOfOptions.find((option) => option.id === idToFind) || undefined;
};

function Members() {
    const { t } = useTranslation(['members', 'parties', 'translation']);
    const { bodyType, zastupitelstvoId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const { basicData, municipalityData: municipality } = useAppContext();
    const response = useLoaderData() as [PartiesData[], unknown, DropdownOptions[], DropdownOptions[]];
    const partiesData = response[0];
    const partyOptions = response[2];
    const memberOptions = response[3];

    const [partyOptionsForSpecificMember, setPartyOptionsForSpecificMember] = useState<DropdownOptions[] | null>(null);
    const [memberOptionsForSpecificParty, setMemberOptionsForSpecificParty] = useState<DropdownOptions[] | null>(null);

    const {organNameOptionLabels} = useLocalizationLabels();

    const organID = bodyType ? municipality.organy[bodyType]?.id : undefined;
    const organNamelabel = capitalizeFirstLetter(organID ? organNameOptionLabels[organID]?.label ?? bodyType : '');


    const selectedBodyTypeData = bodyType ? municipality.organy[bodyType] : undefined;
    const totalCountOfMembersInThisTerm = basicData.pocet_zastupitelu + basicData.pocet_zastupitelek;

    const [filteringState, setFilteringState] = useState({
        member: findOptionById(memberOptions, Number(searchParams.get(URLKeys.member)) || 0) || null,
        selectedParty: findOptionById(partyOptions, Number(searchParams.get(URLKeys.party)) || 0) || null
    });

    const handleFilteringChange = (e: DropdownChangeEvent, key: keyof typeof filteringState) => {
        const newFilteringState = { ...filteringState, [key]: e.value };
        setFilteringState(newFilteringState);

        setSearchParams(
            (prevParams) => {
                const newParams = new URLSearchParams(prevParams);
                if (newFilteringState.selectedParty) {
                    newParams.set(URLKeys.party, newFilteringState.selectedParty.id.toString());
                } else {
                    newParams.delete(URLKeys.party);
                }
                if (newFilteringState.member) {
                    newParams.set(URLKeys.member, newFilteringState.member.id.toString());
                } else {
                    newParams.delete(URLKeys.member);
                }
                return newParams;
            },
            { replace: true }
        );
    };

    useEffect(() => {
        if (filteringState?.member?.id) {
            setPartyOptionsForSpecificMember(
                partiesData
                    .filter((party) => {
                        return party.clenove.find(
                            (member) => Number(member.id) === Number(filteringState.member?.id)
                        );
                    })
                    .map((party) => ({
                        name: party.strana_zkratka,
                        id: party.strana_id
                    }))
            );
        } else {
            setPartyOptionsForSpecificMember(null);
        }
    }, [filteringState?.member, partiesData]);

    useEffect(() => {
        if (filteringState?.selectedParty?.id) {
            const selectedParty = partiesData.find(
                (party) => Number(party.strana_id) === Number(filteringState.selectedParty?.id)
            );
            if (selectedParty) {
                setMemberOptionsForSpecificParty(
                    selectedParty.clenove.map((member) => ({
                        name: member.jmeno,
                        id: member.id
                    }))
                );
            } else {
                setMemberOptionsForSpecificParty(null);
            }
        } else {
            setMemberOptionsForSpecificParty(null);
        }
    }, [filteringState?.selectedParty, partiesData]);

    if (
        basicData !== undefined &&
        partiesData !== undefined &&
        // partiesStats !== undefined &&
        zastupitelstvoId !== undefined &&
        municipality !== undefined &&
        partyOptions !== null
    ) {
        // should be done on BE?

        const partiesWithDerivedData: PartyWithDerivedData[] = partiesData.map((party) => {
            const activeParty = basicData.nejnovejsi_zasedani.mandaty.find(
                (obj: { strana_id: number; pocet_mandatu: number }) => obj.strana_id === party.strana_id
            );

            return {
                ...party,
                aktivni: Boolean(activeParty),
                pocet_mandatu: activeParty?.pocet_mandatu ?? 0
            };
        });

        const repsText = t('inTermComposition.reps', {
            count: totalCountOfMembersInThisTerm
        });

        const descriptionText = `${t('inTermComposition.mandates', {
            type: organNamelabel,
            count: selectedBodyTypeData.pocet_zastupitelu
        })} ${t('inTermComposition.parties', {
            count: partiesData.length,
            repsText: repsText
        })} ${t('inTermComposition.disclaimer')}`;

        const filteringComponents = (
            <>
                <span className='p-float-label'>
                    <Dropdown
                        showClear
                        inputId='member'
                        value={filteringState.member}
                        options={
                            filteringState.selectedParty ? memberOptionsForSpecificParty || [] : memberOptions || []
                        }
                        onChange={(e) => handleFilteringChange(e, 'member')}
                        optionLabel='name'
                        filter
                        filterBy='name'
                    />
                    <label htmlFor='member'>{t('selectMember')}</label>
                </span>
                <span className='p-float-label'>
                    <Dropdown
                        showClear
                        inputId='selectedParty'
                        value={filteringState.selectedParty}
                        options={filteringState.member ? partyOptionsForSpecificMember || [] : partyOptions || []}
                        onChange={(e) => handleFilteringChange(e, 'selectedParty')}
                        optionLabel='name'
                        filter
                        filterBy='name'
                    />
                    <label htmlFor='selectedParty'>{t('selectParty', {ns: 'parties'})}</label>
                </span>
            </>
        );

        return (
            <div className='page-dashboard'>
                <div className='page-header-row'>
                    <ContentSection
                        title={t('members')}
                        level={1}
                        subtitle={basicData.funkcni_obdobi}
                        className='page-title-section'
                    />
                    <div className='stat-chip-row'>
                        <StatChip
                            icon='chair'
                            value={selectedBodyTypeData.pocet_zastupitelu}
                            label={t('availableSeats')}
                        />
                        <StatChip
                            icon='group'
                            value={totalCountOfMembersInThisTerm}
                            label={t('totalReps')}
                        />
                    </div>
                </div>
                <div className='page-description'>{descriptionText}</div>
                <ContentSection level={3} contentClassName='filtering'>{filteringComponents}</ContentSection>
                {partiesWithDerivedData.map((party, i) => {
                    if (
                        filteringState.member &&
                        !party.clenove.find((member) => member.id === filteringState.member?.id)
                    ) {
                        return null;
                    }
                    if (filteringState.selectedParty && party.strana_id !== filteringState.selectedParty.id) {
                        return null;
                    }
                    let memberGroupsData = [];
                    if (party.pocet_aktivnich > 0) {
                        memberGroupsData.push({
                            partyName: party.strana_zkratka,
                            customCountText: t('activeCouncilMembers'),
                            partyColor: party.strana_barva,
                            count: party.pocet_aktivnich,
                            members: party.clenove
                                .filter((clen) => clen.aktivni)
                                .map((clen) => transformMemberData(clen, partiesData))
                        });
                    }
                    if (party.pocet_neaktivnich > 0) {
                        memberGroupsData.push({
                            partyName: party.strana_zkratka,
                            customCountText: t('history', {ns: 'translation'}),
                            partyColor: getColors().darkGray,
                            count: party.pocet_neaktivnich,
                            members: party.clenove
                                .filter((clen) => !clen.aktivni)
                                .map((clen) => transformMemberData(clen, partiesData))
                        });
                    }

                    return (
                        <MemberCardWrapper
                            groupName={party.strana_zkratka}
                            count={party.pocet_mandatu}
                            chipText={t('mandate', {ns:'translation', count: party.pocet_mandatu })}
                            groupCountText=''
                            bgColor={party.strana_barva}
                            partyId={party.strana_id}
                            expanded
                            memberGroupsData={memberGroupsData}
                            key={i}
                            tabIndex={i}
                        />
                    );
                })}
            </div>
        );
    }
}

export default Members;
